import * as React from 'react';
import * as classnames from 'classnames';
import * as _ from 'lodash';
import Toggle from '../../../../common/Toggle';
import {
    UilPlusCircle,
    UilArrowLeft,
    UilMultiply
} from '@iconscout/react-unicons';
import ReactTooltip from 'react-tooltip';
import BatchEditor from './BatchEditor';
import {
    optionType,
    optionSize,
    optionDisplayedInRow,
    imageDisplay
} from '../../../../../mapping/optionSettings';
import Option from './Option';
import {
    MULTIMEDIA_TYPES,
    OptionData,
    OPTION_SIZE,
    OPTION_TYPES,
    ProjectLanguage,
    QTYPES,
    Question,
    QuestionSummary,
    PlainTextContent,
    IMAGE_OPTION_SIZING,
    PROJECT_STATUS
} from '@nexxt/common/types';
import { getAutoOptionsPerRow } from '@nexxt/common/utils/question';
import { SortableContainer, arrayMove } from 'react-sortable-hoc';
import { CustomCheckbox } from '../../../../common/Checkbox/Checkbox';
import { convertToRaw, EditorState } from 'draft-js';
import { getEmptyOptionTexts } from '../buildOptions';
import {
    NA_OPTION_VALUE,
    OTHER_OPTION,
    OTHER_OPTION_VALUE
} from '@nexxt/common/constants';
import NotifyHOC from '../../../../../HOC/Notifications';
import { getConfirmationBox } from '../../../../common/swal/Sweetalert';
import Piping from './Piping';
import Menu from '../../../../common/Menu';
import NumericInput from '../../../../common/NumericInput';
import OptionLibrary from './OptionLibrary';
import Popover from 'react-tiny-popover';
import SaveOptionLibraryPopover from './OptionLibrary/SaveOptionLibraryPopover';
import { getMainLanguage } from '../../../../../utils/project';
import { getFilenameWithoutExtension } from '../../../../../utils/string';
import ConditionOption from './ConditionOption';
import { defaultOptionCondition } from '../../ConditionEditor/default';
import ConditionModal from './ConditionModal';
import { useTranslation } from 'react-i18next';
import {
    createTextMultimediaText,
    uppercaseFirstLetter
} from '@nexxt/common/services/TextService';
import { getDemographicDetails } from '@nexxt/common/demographics/index';
import { DYNATA_QVALUE } from '@nexxt/common/demographics/types';
import ValidationService from '@nexxt/common/services/ValidationService';
import { HELPER_TOOLTIP_MAPPING } from '../../../../../mapping/helperTooltip';
import Loader from '../../../../common/Loader/Loader';
import { dataTestid } from '../../../../../constants/dataTestid';

interface OptionsProps {
    notify: (
        msg: string,
        type: string,
        options?: Record<string, unknown>,
        callback?: () => unknown
    ) => any;
    name?: 'option' | 'button' | 'concept' | 'variable';
    languages: ProjectLanguage[];
    countries: any[];
    question: Question;
    questionSummaries: QuestionSummary[];
    settings?: {
        showMultiplePopup?: boolean;
        forceMultiple?: boolean;
        forceOptionType?: OPTION_TYPES;
        maxOptionNum?: number;
        hideMultiple?: boolean;
        hideNotApplicable?: boolean;
        hideOther?: boolean;
        hideOptionSettings?: boolean;
        hideAdvanceMode?: boolean;
        hideFooter?: boolean;
        hideAdvancedFooter?: boolean;
        hideHeader?: boolean;
        hideEditDesign?: boolean;
        disableDnd?: boolean;
        disableMenu?: boolean;
        showHelper?: boolean;
        forceRandomize?: boolean;
    };
    helperText?: string;
    showError: boolean;
    error?: any;
    disabled: boolean;
    projectHealthOptionIds: number[];
    setQuestion: (keyName: string, value: any) => void;
    setQuestionWithLanguage: (
        keyName: string,
        value: string,
        language: string
    ) => void;
    setMultimediaText: (
        keyName: string,
        value: any,
        language: string,
        index: number,
        type: MULTIMEDIA_TYPES
    ) => void;
    setOtherOptionWithLanguage: (
        keyName: string,
        value: string,
        language: string
    ) => void;
    handleOptions: (options: OptionData[], deletedOptionIds?: number[]) => void;
    setQuestionAsQuestion: (value) => void;
    updatePopupCardType?: (data: QTYPES) => void;
    isLive?: boolean;
    previousPackageQuestion?: any;
    handleTradeoffPiping?: () => void;
    isTradeoffTextPiping?: boolean;
}

const SortableContainerWrapper = SortableContainer(({ children }) => (
    <div className="options--container--body--row">{children}</div>
));

const SortableContainerWrapperWithTopMargin = SortableContainer(
    ({ children }) => (
        <div className="options--container--body--row mt-5 --na">
            {children}
        </div>
    )
);

const Options: React.FC<OptionsProps> = (props: OptionsProps) => {
    const [showAdvancedMode, setShowAdvancedMode] = React.useState(false);
    const [pipingOptions, setPipingOptions] = React.useState([]);
    const [showOptionLibrary, setShowOptionLibrary] = React.useState(
        props.question?.optionSettings?.useOptionpack || false
    );
    const [showBatchEdit, setShowBatchEdit] = React.useState(false);
    const [showOptionLibAddPopover, setShowOptionLibAddPopover] =
        React.useState(false);
    const [editorState, setEditorState] = React.useState(
        EditorState.createEmpty()
    );
    const [focusState, setFocusState] = React.useState(false);
    const [conditionModalSetting, setConditionModalSetting] = React.useState({
        show: false,
        activeOption: null,
        activeOptionIndex: -1,
        isEdit: false,
        editFlag: false
    });
    const selectedOptionType =
        props.settings?.forceOptionType || props.question?.selectedOptionType;
    const [wrapLimit, setWrapLimit] = React.useState(0);
    const [wrapperContainer, setwrapperContainer] = React.useState(0);
    const [windowWidth, setWindowWidth] = React.useState(0);
    const [showEditDesign, setShowEditDesign] = React.useState(false);
    const [optionRowWidth, setOptionRowWidth] = React.useState(0);
    const [isOptionLoading, setIsOptionLoading] =
        React.useState<boolean>(false);
    const { type: questionType, optionSettings } = props.question;

    const getRenderOptions = () => {
        try {
            switch (true) {
                case props.question.piping:
                    return pipingOptions;
                case props.question.isAudience &&
                    !!props.question.builtInID &&
                    props.question.builtInID !== DYNATA_QVALUE.AGE:
                    const q = getDemographicDetails(
                        props.languages,
                        props.question.builtInID,
                        props.countries?.[0].value,
                        null,
                        { questionId: props.question.id as number }
                    );
                    return q?.options;
                default:
                    return props.question.options;
            }
        } catch (err) {
            return props.question.options || [];
        }
    };

    const options = getRenderOptions();
    const totalNumOptions =
        options.length +
        (props.question?.optionSettings?.showOther ? 1 : 0) +
        (props.question?.showNotApplicableOption ? 1 : 0);
    const hasPipingOther =
        props.question.piping &&
        !!options?.find((opt) => opt.value === OTHER_OPTION_VALUE);
    const textOnly = selectedOptionType === OPTION_TYPES.TEXT_ONLY;
    const hasIconSize = [QTYPES.POPUP, QTYPES.SCALE].includes(questionType);
    const editDesignEmpty =
        questionType === QTYPES.POPUP ||
        (questionType === QTYPES.SCALE && textOnly);
    // option ui currently only supports default medium sizing
    const currentOptionSize = OPTION_SIZE.MEDIUM;
    // const currentOptionSize = optionSettings.size || OPTION_SIZE.MEDIUM;
    const optionNumWithOther = props.question?.optionSettings?.showOther
        ? options?.length + 1
        : options?.length;
    const { t } = useTranslation();
    const name = uppercaseFirstLetter(props?.name || 'option');
    const disableOptions =
        props.question.piping ||
        (props.question.isAudience && !!props.question.builtInID) ||
        props.disabled;
    const optionIds = props.question.options.map((option) => option.id);
    const optionsInPreviousPackage =
        (props.previousPackageQuestion &&
            options?.length > 0 &&
            props.previousPackageQuestion.options
                .filter((opt) => optionIds.includes(opt.id))
                .map((filteredOption) => filteredOption.id)) ||
        [];
    const hasCustomSizing = [
        QTYPES.SINGLE_CHOICE,
        QTYPES.MULTIPLE_CHOICE,
        QTYPES.RANK_ORDER
    ].includes(questionType);
    const minRef = React.useRef();
    const maxRef = React.useRef();

    React.useEffect(() => {
        const handleResize = () => {
            if (windowWidth !== window.innerWidth) {
                setWindowWidth(window.innerWidth);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    React.useEffect(() => {
        if (props.isTradeoffTextPiping) {
            confirmPipingOption();
        }
    }, []);

    // React.useEffect(() => {
    //     if (!showBatchEdit) {
    //         const wrapperWidth = document.getElementById(
    //             'options--container--inner'
    //         ).clientWidth;
    //         const containerWidth =
    //             document.getElementById('options--container').clientWidth;

    //         if (textOnly) {
    //             setWrapLimit(
    //                 optionSettings?.numColumns ||
    //                     getAutoOptionsPerRow(
    //                         optionSettings?.size,
    //                         selectedOptionType
    //                     )
    //             );
    //         } else {
    //             const containerLimit = Math.floor(
    //                 containerWidth -
    //                     20 / (IMAGE_OPTION_SIZING[currentOptionSize] + 8)
    //             );
    //             const limitFloor = Math.floor(
    //                 wrapperWidth / (IMAGE_OPTION_SIZING[currentOptionSize] + 8)
    //             );
    //             const limit =
    //                 limitFloor > containerLimit ? containerLimit : limitFloor;

    //             if (containerLimit !== wrapperContainer) {
    //                 setwrapperContainer(containerLimit);
    //             }
    //             const newLimit =
    //                 optionSettings?.numColumns > limit ||
    //                 !optionSettings?.numColumns
    //                     ? limit
    //                     : optionSettings?.numColumns;
    //             setWrapLimit(newLimit);
    //         }
    //     }
    // }, [windowWidth, options, optionSettings?.numColumns, selectedOptionType]);

    // React.useEffect(() => {
    //     const sizing = textOnly
    //         ? optionSettings?.size || OPTION_SIZE.MEDIUM
    //         : currentOptionSize;

    //     const numColumns = optionSettings?.numColumns
    //         ? optionSettings?.numColumns
    //         : getAutoOptionsPerRow(sizing, selectedOptionType);
    //     const optionSize = IMAGE_OPTION_SIZING[sizing.toUpperCase()];
    //     const gap = 8;
    //     const width = numColumns * optionSize + gap * (numColumns - 1);
    //     const containerWidth =
    //         document.getElementById('options--container').clientWidth;
    //     const newOptionRowWidth =
    //         width > containerWidth ? containerWidth : width;
    //     setOptionRowWidth(newOptionRowWidth);
    // }, [optionSettings?.numColumns, wrapperContainer, wrapLimit]);

    const handleClickOutside = (event) => {
        if (
            minRef.current &&
            !(minRef.current as any).contains(event.target) &&
            maxRef.current &&
            !(maxRef.current as any).contains(event.target)
        ) {
            setFocusState(false);
        }
    };

    React.useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    /** PIPING OPTION HANDLER  */
    const confirmPipingOption = async () => {
        setIsOptionLoading(true);
        let isConfirmed = true;
        if (!!optionNumWithOther) {
            const confirm = await getConfirmationBox({
                message:
                    "<p>Enabling piping will delete current options you've created.</p><p>You will not be able to edit piped options or add additional options to the piped options.</p><p>Would you like to continue? </p>",
                showCloseButton: true,
                title: 'Start Over?',
                messageAligned: 'left'
            });
            isConfirmed = confirm.isConfirmed;
        }

        if (isConfirmed) {
            const options = props.question.options;
            const optionIds = options
                .filter((opt) => opt.id)
                .map((opt) => opt.id);

            const optionsWithoutIds = options.map((opt) => {
                delete opt.id;
                return opt;
            });
            props.setQuestionAsQuestion({
                ...props.question,
                piping: true,
                options: [],
                deletedOptionIDs: [
                    ...(props.question.deletedOptionIDs || []),
                    ...optionIds
                ],
                showNotApplicableOption: false,
                optionSettings: {
                    ...props.question.optionSettings,
                    showOther: false,
                    showSpecify: false,
                    optionHistory: {
                        ...(props.question.optionSettings?.optionHistory || {}),
                        legacyOptions: optionsWithoutIds,
                        selectedOptionType: props.question?.selectedOptionType,
                        min: props.question?.min,
                        max: props.question?.max,
                        notApplicableTexts: props.question?.notApplicableTexts,
                        showNotApplicableOption:
                            props.question?.showNotApplicableOption,
                        random: props.question?.random,
                        showOther: props.question?.optionSettings?.showOther,
                        showSpecify:
                            props.question?.optionSettings?.showSpecify,
                        otherOption: props.question?.optionSettings?.otherOption
                    }
                }
            });
        } else {
        }
        setIsOptionLoading(false);
    };

    const cancelPiping = () => {
        const legacyOptions =
            _.cloneDeep(
                props.question.optionSettings.optionHistory.legacyOptions
            ) || [];
        const resetQuestion = resetOptionHistory();
        setPipingOptions([]);
        props.setQuestionAsQuestion({
            ...resetQuestion,
            piping: false,
            pipingQuestions: [],
            options: legacyOptions
        });
        props?.handleTradeoffPiping();
    };
    /** END PIPING OPTION HANDLER  */

    /** OPTION LIBRARY HANDLER */
    const confirmOptionLibrary = async () => {
        let isConfirmed = true;
        if (!!optionNumWithOther) {
            const confirm = await getConfirmationBox({
                message:
                    "<p>Enabling library will override current options you've created.</p><p>Would you like to continue? </p>",
                showCloseButton: true,
                title: 'Start Over?',
                messageAligned: 'left'
            });
            isConfirmed = confirm.isConfirmed;
        }
        if (isConfirmed) {
            const optionIds = options
                .filter((opt) => opt.id)
                .map((opt) => opt.id);

            const optionsWithoutIds = options.map((opt) => {
                delete opt.id;
                return opt;
            });
            props.setQuestionAsQuestion({
                ...props.question,
                options: [],
                deletedOptionIDs: [
                    ...(props.question.deletedOptionIDs || []),
                    ...optionIds
                ],
                showNotApplicableOption: false,
                optionSettings: {
                    ...props.question.optionSettings,
                    useOptionpack: true,
                    showOther: false,
                    showSpecify: false,
                    optionHistory: {
                        ...(props.question.optionSettings?.optionHistory || {}),
                        legacyOptions: optionsWithoutIds,
                        selectedOptionType: props.question?.selectedOptionType,
                        min: props.question?.min,
                        max: props.question?.max,
                        notApplicableTexts: props.question?.notApplicableTexts,
                        showNotApplicableOption:
                            props.question?.showNotApplicableOption,
                        random: props.question?.random,
                        showOther: props.question?.optionSettings?.showOther,
                        showSpecify:
                            props.question?.optionSettings?.showSpecify,
                        otherOption: props.question?.optionSettings?.otherOption
                    }
                }
            });
            setShowOptionLibrary(true);
        }
    };

    const cancelOptionLibrary = () => {
        const srcOptions = props.question.options || [];
        const srcOptionIds = srcOptions.map((opt) => opt.id).filter((x) => x);
        const legacyOptions =
            _.cloneDeep(
                props.question.optionSettings.optionHistory.legacyOptions
            ) || [];
        const resetQuestion = resetOptionHistory();
        const options = legacyOptions.map((opt, index) => {
            let srcOptionId = undefined;
            if (srcOptionIds?.length) {
                srcOptionId = srcOptionIds.shift();
            }

            return { ...opt, id: srcOptionId };
        });
        props.setQuestionAsQuestion({
            ...resetQuestion,
            piping: false,
            pipingQuestions: [],
            options,
            deletedOptionIDs: [
                ...(props.question.deletedOptionIDs || []),
                ...srcOptionIds
            ],
            optionSettings: {
                ...resetQuestion.optionSettings,
                useOptionpack: false,
                optionpackId: null
            }
        });
        showOptionLibrary && setShowOptionLibrary(false);
    };
    /** END OPTION LIBRARY HANDLER */

    /** BATCH EDITOR HANDLER */
    const handleBatchImageUpload = (media: any) => {
        const options = [];
        const currOptions = props.question.options || [];
        const currOptionIds = currOptions.map((opt) => opt.id);
        //batch edit
        const mainLanguageCode = getMainLanguage(props.languages);
        media.forEach((media, index) => {
            const label = getFilenameWithoutExtension(media.label);
            const id = currOptionIds?.shift() || undefined;

            options.push({
                id,
                label,
                value: options.length + index + 1,
                mediaURL: media.mediaURL,
                texts: getEmptyOptionTexts(props.languages).map(
                    (textTranslation) => {
                        if (textTranslation.language == mainLanguageCode) {
                            return { ...textTranslation, text: label };
                        }
                        return textTranslation;
                    }
                ),
                randomizeOption: false,
                empty: false
            });
        });
        if (showOptionLibrary) {
            setShowOptionLibrary(false);
            props.setQuestionAsQuestion({
                ...props.question,
                options,
                deletedOptionIDs: [
                    ...props.question.deletedOptionIDs,
                    ...currOptionIds
                ],
                optionSettings: {
                    ...props.question.optionSettings,
                    useOptionpack: false,
                    optionpackId: null
                }
            });
        } else {
            props.handleOptions(options, currOptionIds);
        }
    };

    const handleBatchOption = () => {
        if (!showBatchEdit) {
            setShowBatchEdit(true);
            return;
        }
        const srcOptions = _.cloneDeep(props.question.options);
        let deleteOptionId = [];
        const { entityMap } = convertToRaw(editorState.getCurrentContent());

        const entities = Object.values(entityMap);
        const partialOptions = entities.map(
            (entity): any => entity?.['data']
        ) as Partial<OptionData>[];
        const mainLanguage = getMainLanguage(props.languages);
        const options: OptionData[] = partialOptions.map((opt, index) => {
            const srcOption = srcOptions?.[index];
            return {
                id: srcOption?.id ? srcOption.id : undefined,
                label: opt?.label,
                value: String(index + 1),
                mediaURL: opt?.mediaURL,
                texts: getEmptyOptionTexts(props.languages)
                    .map((textTranslation) => {
                        const currLanguageText = srcOption?.texts?.find(
                            (text) =>
                                text?.language === textTranslation.language
                        );

                        if (textTranslation.language === mainLanguage) {
                            return {
                                ...(currLanguageText || textTranslation),
                                contents: [
                                    {
                                        ...currLanguageText?.contents?.[0],
                                        type: MULTIMEDIA_TYPES.TEXT,
                                        text: opt?.label
                                    }
                                ]
                            };
                        }

                        return currLanguageText || textTranslation;
                    })
                    .filter((x) => x),
                randomizeOption: srcOption?.randomizeOption
                    ? srcOption?.randomizeOption
                    : false,
                conditions: srcOption?.conditions?.length
                    ? srcOption?.conditions
                    : [],
                empty: false
            } as OptionData;
        });

        if (options.length < srcOptions.length) {
            deleteOptionId = [
                ...srcOptions.slice(options.length).map((opt) => opt.id)
            ];
        }

        const hasMedias = options.some((opt) => opt.mediaURL);
        props.setQuestionAsQuestion({
            ...props.question,
            options,
            deleteOptionId: [
                ...(props.question.deletedOptionIDs || []),
                ...deleteOptionId
            ],
            selectedOptionType: hasMedias
                ? OPTION_TYPES.IMAGE_ONLY
                : OPTION_TYPES.TEXT_ONLY
        });

        setEditorState(EditorState.createEmpty());
        setShowBatchEdit(false);
    };
    /** END BATCH EDITOR HANDLER */

    /** OPTION HANDLER  */
    const getMaxWording = (): string => {
        const { max } = props.question;
        return max === optionNumWithOther ? `${max}(max)` : `${max}`;
    };

    const isOptionInOrder = (options: OptionData[]) => {
        return options.every((opt, index) => opt.value == String(index + 1));
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const shouldRearrange = isOptionInOrder(props.question.options);
        let newOptions = arrayMove(props.question.options, oldIndex, newIndex);

        if (shouldRearrange) {
            newOptions = newOptions.map((opt, index) => ({
                ...opt,
                value: String(index + 1)
            }));
        }

        props.setQuestion('options', newOptions);
    };

    // To block dnd on specific tags and classnames
    const shouldCancelStart = (e) => {
        if (
            ['input', 'textarea', 'svg', 'path'].indexOf(
                e.target.tagName.toLowerCase()
            ) !== -1
        ) {
            return true;
        }
        if (
            ['option--image', 'hover-container', 'option--icon'].indexOf(
                e.target.className
            ) !== -1
        ) {
            return true;
        }
        if (e?.target?.className?.includes('option--text')) {
            return true;
        }
        return false;
    };

    const isOptionEmpty = (opt: OptionData) => {
        const validationService = new ValidationService();
        let empty = true;
        if (!validationService.isEmpty(opt?.label)) {
            empty = false;
        }

        if (!validationService.isEmpty(opt?.mediaURL)) {
            empty = false;
        }

        opt?.texts?.forEach((text) => {
            if (
                !validationService.isEmpty(
                    (text?.contents?.[0] as PlainTextContent)?.text
                )
            ) {
                empty = false;
            }
        });
        return empty;
    };
    const addNewOption = (
        position = props.question?.options?.length || 0,
        data?
    ) => {
        const currOptions = props.question?.options || [];
        const currMax = props.question.max || 0;
        let newOption = {
            label: data?.label || '',
            value: String(position + 1),
            mediaURL: data?.mediaURL || null,
            texts: getEmptyOptionTexts(props.languages),
            randomizeOption:
                props.question?.random || props.settings?.forceRandomize,
            empty: !data
        } as OptionData;

        if (selectedOptionType === OPTION_TYPES.CONDITION) {
            newOption = {
                ...newOption,
                conditions: [defaultOptionCondition()]
            };
        }
        const newOptions = (
            [
                ...currOptions.slice(0, position),
                newOption,
                ...currOptions.slice(position)
            ] as OptionData[]
        ).map((opt, idx) => ({ ...opt, value: String(idx + 1) }));

        currMax == optionNumWithOther &&
            props.setQuestion(
                'max',
                props.question?.optionSettings?.showOther
                    ? newOptions.length + 1
                    : newOptions.length
            );

        if (showOptionLibrary) {
            setShowOptionLibrary(false);
            props.setQuestionAsQuestion({
                ...props.question,
                options: newOptions,
                optionSettings: {
                    ...props.question.optionSettings,
                    useOptionpack: false,
                    optionpackId: null
                }
            });
        } else {
            props.handleOptions(newOptions);
        }
    };

    const updateOption = (position: number, keyName: string, value: any) => {
        const options = props.question.options;

        options[position] = {
            ...options[position],
            empty: false,
            [keyName]: value
        } as OptionData;

        if (showOptionLibrary) {
            setShowOptionLibrary(false);
            props.setQuestionAsQuestion({
                ...props.question,
                options,
                optionSettings: {
                    ...props.question.optionSettings,
                    useOptionpack: false,
                    optionpackId: null
                }
            });
        } else {
            props.handleOptions(options);
        }
    };

    const deleteOption = async (position) => {
        const options = props.question.options;
        const currOption = options[position];
        let isValid = isOptionEmpty(currOption);
        if (!isValid) {
            await getConfirmationBox({
                showCloseButton: true,
                showCancelButton: true,
                title: 'Delete Option?',
                message: 'This action cannot be undone.'
            }).then((x) => {
                isValid = x.isConfirmed;
            });
        }

        if (isValid) {
            const newOptions = (
                [
                    ...options.slice(0, position),
                    ...options.slice(position + 1)
                ] as OptionData[]
            ).map((opt, idx) => ({ ...opt, value: String(idx + 1) }));

            if (showOptionLibrary) {
                setShowOptionLibrary(false);
                props.setQuestionAsQuestion({
                    ...props.question,
                    optionSettings: {
                        ...props.question.optionSettings,
                        useOptionpack: false,
                        optionpackId: null
                    }
                });
            }
            const optionNumWithOther = props.question?.optionSettings?.showOther
                ? newOptions.length + 1
                : newOptions.length;
            const max =
                props.question.max > optionNumWithOther
                    ? optionNumWithOther
                    : props.question.max;
            props.setQuestion('max', max);
            props.handleOptions(
                newOptions,
                currOption?.id ? [currOption.id] : []
            );

            conditionModalSetting?.activeOption?.id === currOption?.id &&
                setConditionModalSetting({
                    show: false,
                    activeOption: null,
                    activeOptionIndex: -1,
                    isEdit: false,
                    editFlag: false
                });
        }
    };

    const updateOptionTextsByLanguage = (
        position: number,
        value: string,
        language: string
    ) => {
        const options = _.cloneDeep(props.question.options);
        const currOption = { ...options[position], empty: false };

        const languageIndex = currOption.texts.findIndex(
            (text) => text.language === language
        );
        const mainLanguageCode = getMainLanguage(props.languages);
        // Update the option label if user modifies the main language option texts and the current option text is same to the label
        if (
            mainLanguageCode == language &&
            (currOption.texts[languageIndex].contents[0] as PlainTextContent)
                .text == currOption.label
        ) {
            currOption.label = value;
        }

        if (languageIndex > -1) {
            (
                currOption.texts[languageIndex].contents[0] as PlainTextContent
            ).text = value;
        } else {
            currOption.texts.push({
                language,
                contents: [{ type: MULTIMEDIA_TYPES.TEXT, text: value }]
            });
        }
        options[position] = currOption;
        if (showOptionLibrary) {
            setShowOptionLibrary(false);
            props.setQuestionAsQuestion({
                ...props.question,
                optionSettings: {
                    ...props.question.optionSettings,
                    useOptionpack: false,
                    optionpackId: null
                }
            });
        }
        props.handleOptions(options);
    };

    const resetOptionHistory = () => {
        const { optionSettings } = props.question;
        const { optionHistory } = optionSettings;
        return {
            ...props.question,
            selectedOptionType: optionHistory.selectedOptionType,
            min: optionHistory.min,
            max: optionHistory.max,
            notApplicableTexts: optionHistory.notApplicableTexts,
            showNotApplicableOption: optionHistory.showNotApplicableOption,
            random: optionHistory.random,
            optionSettings: {
                ...props.question.optionSettings,
                showOther: optionHistory.showOther,
                showSpecify: optionHistory.showSpecify,
                otherOption: optionHistory.otherOption,
                optionHistory: {}
            }
        };
    };

    const updateOptionSettings = (keyName: string, value: any) => {
        const optionSettings = {
            ...props.question.optionSettings,
            [keyName]: value
        };

        // Checks if current options per row is compatiable with the option size - if not set to 0/Auto
        if (
            keyName === 'size' &&
            [
                OPTION_SIZE.SMALL,
                OPTION_SIZE.MEDIUM,
                OPTION_SIZE.LARGE,
                OPTION_SIZE.XL
            ].includes(value)
        ) {
            const desktopOptions = determineValidOptionsPerRow(
                optionDisplayedInRow,
                IMAGE_OPTION_SIZING[value]
            );
            const mobileOptions = determineValidOptionsPerRow(
                optionDisplayedInRow.filter((opt) => opt.value <= 5),
                IMAGE_OPTION_SIZING[value]
            );
            optionSettings.numColumns > 0 &&
                desktopOptions.forEach((x) => {
                    if (
                        optionSettings?.numColumns === x.value &&
                        'disabled' in x
                    ) {
                        optionSettings.numColumns = 0;
                    }
                });
            optionSettings.numColumnsMobile > 0 &&
                mobileOptions.forEach((x) => {
                    if (
                        optionSettings?.numColumnsMobile === x.value &&
                        'disabled' in x
                    ) {
                        optionSettings.numColumnsMobile = 0;
                    }
                });
        }

        if (showOptionLibrary) {
            setShowOptionLibrary(false);
            props.setQuestionAsQuestion({
                ...props.question,
                optionSettings: {
                    ...props.question.optionSettings,
                    useOptionpack: false,
                    optionpackId: null
                }
            });
        } else {
            props.setQuestionAsQuestion({
                ...props.question,
                optionSettings
            });
        }
    };

    const updateOtherOption = (keyName: string, value: any) => {
        const otherOption = {
            ...props.question?.optionSettings?.otherOption,
            [keyName]: value
        };

        if (showOptionLibrary) {
            setShowOptionLibrary(false);
            props.setQuestionAsQuestion({
                ...props.question,
                optionSettings: {
                    ...props.question.optionSettings,
                    otherOption,
                    useOptionpack: false,
                    optionpackId: null
                }
            });
        } else {
            updateOptionSettings('otherOption', otherOption);
        }
    };
    const toggleOtherSpecify = async (toggle = true) => {
        if (!toggle) {
            const { isConfirmed } = await getConfirmationBox({
                showCloseButton: true,
                showCancelButton: true,
                title: 'Delete Option?',
                message: 'This action cannot be undone.'
            });

            if (!isConfirmed) return;
        }

        const texts = props.question?.optionSettings?.otherOption?.texts?.length
            ? props.question?.optionSettings?.otherOption?.texts
            : createTextMultimediaText(t, props.languages, 'BUTTON.OTHER', '');
        const optionSettings = {
            ...props.question.optionSettings,
            showOther: toggle,
            showSpecify: toggle ? true : false,
            otherOption: toggle ? { ...OTHER_OPTION, texts } : null
        };
        if (showOptionLibrary) {
            setShowOptionLibrary(false);
            props.setQuestionAsQuestion({
                ...props.question,
                optionSettings: {
                    ...props.question.optionSettings,
                    useOptionpack: false,
                    optionpackId: null
                }
            });
        }
        props.setQuestion('optionSettings', optionSettings);
    };
    /** END OPTION HANDLER  */

    /** OPTION CONDITION HANDLER */
    const confirmOptionCondition = (conditions, deleteConditionIDs = []) => {
        updateOption(
            conditionModalSetting.activeOptionIndex,
            'conditions',
            conditions
        );
        updateOption(
            conditionModalSetting.activeOptionIndex,
            'deleteConditionIDs',
            deleteConditionIDs
        );
        setConditionModalSetting({
            isEdit: false,
            show: false,
            activeOption: null,
            activeOptionIndex: -1,
            editFlag: false
        });
    };

    /** END OPTION CONDITION HANDLER */

    const updateNotApplicable = () => {
        const notApplicableTexts = props.question.notApplicableTexts?.length
            ? props.question.notApplicableTexts
            : createTextMultimediaText(
                  t,
                  props.languages,
                  'BUTTON.NOT_APPLICABLE',
                  ''
              );

        props.setQuestionAsQuestion({
            ...props.question,
            showNotApplicableOption: true,
            notApplicableTexts
        });
    };
    const multipleChoiceCheckboxBody = () => {
        const multiplePopup =
            questionType === QTYPES.POPUP &&
            props.question?.popupCards?.every(
                (card) => card.type === QTYPES.MULTI_POPUP_CARD
            );

        return questionType === QTYPES.MULTIPLE_CHOICE ||
            props.settings?.forceMultiple ||
            multiplePopup ? (
            <>
                <span className="custom-checkbox-container --label">
                    Select
                </span>
                <NumericInput
                    ref={minRef}
                    setFocus={setFocusState}
                    name="min"
                    placeholder="min"
                    value={props.question.min}
                    handleChange={(value) => props.setQuestion('min', value)}
                    min={1}
                    max={optionNumWithOther}
                    handleIncrement={() => {
                        props.setQuestion(
                            'min',
                            props.question.min < props.question.max ||
                                props.question.min < optionNumWithOther
                                ? props.question.min + 1
                                : props.question.min
                        );
                    }}
                    handleDecrement={() => {
                        props.setQuestion(
                            'min',
                            props.question.min <= 1 ? 1 : props.question.min - 1
                        );
                    }}
                />

                <span>-</span>
                <NumericInput
                    ref={maxRef}
                    setFocus={setFocusState}
                    name="max"
                    placeholder="max"
                    value={props.question.max}
                    handleChange={(value) => {
                        if (value > optionNumWithOther) {
                            value = optionNumWithOther;
                        }
                        props.setQuestion('max', value);
                    }}
                    min={1}
                    max={optionNumWithOther}
                    handleIncrement={() => {
                        props.setQuestion(
                            'max',
                            props.question.max < optionNumWithOther
                                ? props.question.max + 1
                                : props.question.max
                        );
                    }}
                    handleDecrement={() => {
                        props.setQuestion(
                            'max',
                            props.question.max > props.question.min
                                ? props.question.max - 1
                                : props.question.max
                        );
                    }}
                    enableMaxLabel
                />
            </>
        ) : null;
    };

    const determineValidOptionsPerRow = (
        options,
        selectedSize = IMAGE_OPTION_SIZING[optionSettings?.size] ||
            OPTION_SIZE.AUTO
    ) => {
        const maxBotWidth = 1100;
        if (selectedSize === OPTION_SIZE.AUTO) return options;
        return options.map((x) => {
            if (x.value > Math.floor(maxBotWidth / selectedSize)) {
                return { ...x, disabled: true };
            }
            return x;
        });
    };
    return (
        <div className="row">
            <div className="options--container col-md-12">
                {conditionModalSetting?.show &&
                    conditionModalSetting.activeOption && (
                        <ConditionModal
                            option={conditionModalSetting.activeOption}
                            onSave={confirmOptionCondition}
                            questionSummaries={props.questionSummaries}
                            onClose={() =>
                                setConditionModalSetting({
                                    isEdit: false,
                                    show: false,
                                    activeOption: null,
                                    activeOptionIndex: -1,
                                    editFlag: false
                                })
                            }
                            disabled={props.disabled}
                            languages={props.languages}
                            showError={props.showError}
                            conditionModalSetting={conditionModalSetting}
                            setConditionModalSetting={setConditionModalSetting}
                        />
                    )}
                {!props.settings?.hideFooter &&
                    !(
                        props.question?.tradeoffSettings?.tradeoffTypes ==
                        OPTION_TYPES.TEXT_ONLY
                    ) && (
                        <div className="form-group-header">
                            {`${name}s`} &amp; {`${name}`} Settings{'  '}
                            {props.settings?.showHelper && (
                                <i
                                    className={classnames('', {
                                        error:
                                            props.showError &&
                                            !!props?.error?.optionCard
                                    })}
                                >
                                    {props?.helperText}
                                </i>
                            )}
                        </div>
                    )}
                <div className="options--container--footer--container">
                    <div className="row options--container--footer--container--wrapper">
                        <div className="row">
                            {(questionType === QTYPES.VIRTUAL ||
                                (!props.settings?.hideFooter &&
                                    !showBatchEdit)) &&
                                !(
                                    props.question?.tradeoffSettings
                                        ?.tradeoffTypes ==
                                    OPTION_TYPES.TEXT_ONLY
                                ) && (
                                    <button
                                        className="btn btn--md btn--black"
                                        onClick={() => addNewOption()}
                                        disabled={
                                            (props.settings?.maxOptionNum &&
                                                props.settings?.maxOptionNum <=
                                                    props.question.options
                                                        .length) ||
                                            props.disabled ||
                                            props.question.piping ||
                                            showBatchEdit
                                        }
                                    >
                                        <span>{`Add New ${name}`}</span>
                                    </button>
                                )}
                            {!props.settings?.hideFooter &&
                                !props.settings?.hideAdvancedFooter &&
                                !(props.question.type === QTYPES.TRADE_OFF) && (
                                    <>
                                        <button
                                            className="btn btn--md btn--black"
                                            onClick={() => handleBatchOption()}
                                            disabled={
                                                props.question.piping ||
                                                props.disabled
                                            }
                                        >
                                            {showBatchEdit ? (
                                                <>
                                                    <UilArrowLeft size={15} />{' '}
                                                    <span>Confirm</span>
                                                </>
                                            ) : (
                                                <span>Batch Add</span>
                                            )}
                                        </button>

                                        <button
                                            className={classnames(
                                                'btn btn--md btn--black'
                                            )}
                                            data-test={dataTestid.LIBRARY_BTN}
                                            disabled={
                                                props.question.piping ||
                                                showBatchEdit ||
                                                showOptionLibrary ||
                                                props.disabled
                                            }
                                            onClick={() =>
                                                confirmOptionLibrary()
                                            }
                                        >
                                            Add From Library
                                        </button>

                                        <Popover
                                            isOpen={showOptionLibAddPopover}
                                            onClickOutside={() =>
                                                setShowOptionLibAddPopover(
                                                    false
                                                )
                                            }
                                            align="center"
                                            position="bottom"
                                            content={
                                                <SaveOptionLibraryPopover
                                                    question={props.question}
                                                    languages={props.languages}
                                                    onClose={() =>
                                                        setShowOptionLibAddPopover(
                                                            false
                                                        )
                                                    }
                                                />
                                            }
                                        >
                                            <button
                                                className="btn btn--md btn--blue--outline"
                                                disabled={
                                                    showOptionLibrary ||
                                                    props.disabled ||
                                                    props.question?.piping
                                                }
                                                onClick={() =>
                                                    setShowOptionLibAddPopover(
                                                        true
                                                    )
                                                }
                                            >
                                                Save Options to Library
                                            </button>
                                        </Popover>

                                        {!props.settings?.hideOther && (
                                            <span
                                                className={classnames(
                                                    'btn btn--xs btn--transparent',
                                                    {
                                                        disabled:
                                                            showBatchEdit ||
                                                            props.question
                                                                .optionSettings
                                                                ?.showOther
                                                    }
                                                )}
                                                onClick={() =>
                                                    !props.disabled &&
                                                    toggleOtherSpecify(true)
                                                }
                                            >
                                                <UilPlusCircle size={20} />
                                                Other, Specify
                                            </span>
                                        )}
                                        {!props.settings?.hideNotApplicable && (
                                            <span
                                                className={classnames(
                                                    'btn btn--xs btn--transparent',
                                                    {
                                                        disabled:
                                                            showBatchEdit ||
                                                            props.question
                                                                .showNotApplicableOption ||
                                                            props.disabled
                                                    }
                                                )}
                                                onClick={updateNotApplicable}
                                            >
                                                <UilPlusCircle size={20} />
                                                Not Applicable
                                            </span>
                                        )}
                                    </>
                                )}
                        </div>
                        {!props.settings?.hideFooter &&
                            !props.settings?.hideAdvancedFooter &&
                            !(
                                props.question?.tradeoffSettings
                                    ?.tradeoffTypes == OPTION_TYPES.TEXT_ONLY
                            ) && (
                                <div
                                    className="row"
                                    data-tip="custom show"
                                    data-for="pipe-option-helper"
                                    data-delay-show="500"
                                    style={{ paddingBottom: 4 }}
                                >
                                    <Toggle
                                        checked={props.question.piping}
                                        disabled={
                                            props.question.piping ||
                                            props.disabled
                                        }
                                        onChange={() => {
                                            confirmPipingOption();
                                        }}
                                        label="PIPED OPTIONS"
                                    />
                                    <ReactTooltip
                                        className="tooltip"
                                        id="pipe-option-helper"
                                        aria-haspopup="true"
                                        effect="solid"
                                        place="top"
                                    >
                                        <span>
                                            Piped options allow the user to pipe
                                            answer choices from a previous
                                            question
                                        </span>
                                    </ReactTooltip>
                                </div>
                            )}
                    </div>
                </div>

                {!showBatchEdit ? (
                    <>
                        {props.question.piping && (
                            <Piping
                                question={props.question}
                                languages={props.languages}
                                countries={props.countries}
                                setQuestionAsQuestion={
                                    props.setQuestionAsQuestion
                                }
                                setPipingOptions={setPipingOptions}
                                onClose={cancelPiping}
                                disabled={props.disabled}
                            />
                        )}
                        {showOptionLibrary && (
                            <OptionLibrary
                                question={props.question}
                                onClose={cancelOptionLibrary}
                                setQuestionAsQuestion={
                                    props.setQuestionAsQuestion
                                }
                                disabled={props.disabled}
                                toggleOptionLoader={(toggle: boolean) =>
                                    setIsOptionLoading(toggle)
                                }
                            />
                        )}
                        <div
                            className="options--container--body--container"
                            id="options--container"
                        >
                            <div
                                id="options--container--inner"
                                style={{ width: '100%' }}
                            >
                                {/** body goes here*/}
                                {/* <div className="options--container--body--row"> */}
                                {options.length < 1 && (
                                    <div className="options--container--inner--placeholder">
                                        {`Add new options by clicking "Add New ${name}"
                                    above. ${
                                        !props.settings?.hideAdvancedFooter &&
                                        !(
                                            props.question.type ===
                                            QTYPES.TRADE_OFF
                                        )
                                            ? `You may also add multiple options by
                                    clicking "Batch Add" or "Add from Library".`
                                            : ''
                                    }`}
                                    </div>
                                )}
                                <SortableContainerWrapper
                                    axis="xy"
                                    onSortEnd={onSortEnd}
                                    helperClass="options--drag--helper"
                                    shouldCancelStart={shouldCancelStart}
                                >
                                    <>
                                        {isOptionLoading ? (
                                            <Loader isInline={false} />
                                        ) : null}
                                        {options?.map((option, index) => {
                                            const existsInPreviousPackage =
                                                optionsInPreviousPackage.length >
                                                    0 &&
                                                optionsInPreviousPackage.includes(
                                                    option.id
                                                );

                                            if (
                                                props.question.type ===
                                                QTYPES.VIRTUAL
                                            ) {
                                                return (
                                                    <ConditionOption
                                                        key={`option-${index}`}
                                                        languages={
                                                            props.languages
                                                        }
                                                        question={
                                                            props.question
                                                        }
                                                        questionSummaries={
                                                            props.questionSummaries
                                                        }
                                                        option={option}
                                                        handleOptions={
                                                            props.handleOptions
                                                        }
                                                        updateOption={(
                                                            keyName,
                                                            value
                                                        ) =>
                                                            updateOption(
                                                                index,
                                                                keyName,
                                                                value
                                                            )
                                                        }
                                                        updateOptionTextsByLanguage={(
                                                            value,
                                                            language
                                                        ) =>
                                                            updateOptionTextsByLanguage(
                                                                index,
                                                                value,
                                                                language
                                                            )
                                                        }
                                                        deleteOption={() =>
                                                            deleteOption(index)
                                                        }
                                                        optionDisabled={
                                                            disableOptions
                                                        }
                                                        errorObject={
                                                            (props.error
                                                                ?.options ||
                                                                {})[index]
                                                        }
                                                        conditionErrorObject={
                                                            (props.error
                                                                ?.conditions ||
                                                                [])?.[index]
                                                        }
                                                        showError={
                                                            props.showError &&
                                                            !props.question
                                                                .piping
                                                        }
                                                        showProjectHealth={props.projectHealthOptionIds?.includes(
                                                            option?.id
                                                        )}
                                                        isLive={props.isLive}
                                                        existsInPreviousPackage={
                                                            existsInPreviousPackage
                                                        }
                                                    />
                                                );
                                            }

                                            return (
                                                <Option
                                                    languages={props.languages}
                                                    key={`option-${index}`}
                                                    question={props.question}
                                                    option={option}
                                                    optionType={
                                                        selectedOptionType as OPTION_TYPES
                                                    }
                                                    showAdvancedMode={
                                                        showAdvancedMode
                                                    }
                                                    handleOptions={
                                                        props.handleOptions
                                                    }
                                                    updateOption={(
                                                        keyName,
                                                        value
                                                    ) =>
                                                        updateOption(
                                                            index,
                                                            keyName,
                                                            value
                                                        )
                                                    }
                                                    addNewOption={(position) =>
                                                        addNewOption(position)
                                                    }
                                                    updateOptionTextsByLanguage={(
                                                        value,
                                                        language
                                                    ) =>
                                                        updateOptionTextsByLanguage(
                                                            index,
                                                            value,
                                                            language
                                                        )
                                                    }
                                                    deleteOption={() =>
                                                        deleteOption(index)
                                                    }
                                                    index={index}
                                                    position={index}
                                                    optionDisabled={
                                                        disableOptions
                                                    }
                                                    disabled={
                                                        disableOptions ||
                                                        props.settings
                                                            ?.disableDnd ||
                                                        existsInPreviousPackage
                                                    }
                                                    errorObject={
                                                        (props.error?.options ||
                                                            {})[index]
                                                    }
                                                    showError={
                                                        props.showError &&
                                                        !props.question.piping
                                                    }
                                                    showProjectHealth={props.projectHealthOptionIds?.includes(
                                                        option?.id
                                                    )}
                                                    conditionModalSetting={
                                                        conditionModalSetting
                                                    }
                                                    setConditionModalSetting={(
                                                        setting
                                                    ) =>
                                                        setConditionModalSetting(
                                                            {
                                                                ...setting,
                                                                activeOptionIndex:
                                                                    index
                                                            }
                                                        )
                                                    }
                                                    settings={props.settings}
                                                    isLive={props.isLive}
                                                    existsInPreviousPackage={
                                                        existsInPreviousPackage
                                                    }
                                                />
                                            );
                                        })}
                                        {props.question.optionSettings
                                            ?.showOther && (
                                            <Option
                                                languages={props.languages}
                                                showAdvancedMode={
                                                    showAdvancedMode
                                                }
                                                question={props.question}
                                                optionType={
                                                    selectedOptionType as OPTION_TYPES
                                                }
                                                isBuiltInOption
                                                option={{
                                                    ...props.question
                                                        .optionSettings
                                                        ?.otherOption,
                                                    texts:
                                                        props.question
                                                            .optionSettings
                                                            ?.otherOption
                                                            ?.texts || [],
                                                    mediaURL:
                                                        props.question
                                                            .optionSettings
                                                            ?.otherOption
                                                            ?.mediaURL || ''
                                                }}
                                                updateOption={updateOtherOption}
                                                updateOptionSettings={(
                                                    keyName,
                                                    value
                                                ) => {
                                                    updateOptionSettings(
                                                        keyName,
                                                        value
                                                    );
                                                }}
                                                deleteOption={() =>
                                                    toggleOtherSpecify(false)
                                                }
                                                updateOptionTextsByLanguage={(
                                                    value: string,
                                                    language: string
                                                ) =>
                                                    props.setOtherOptionWithLanguage(
                                                        'texts',
                                                        value,
                                                        language
                                                    )
                                                }
                                                index={-1}
                                                disabled // props for sortableElement HOC
                                                errorObject={{
                                                    texts: props.error
                                                        ?.otherTexts,
                                                    mediaURL:
                                                        props.error?.otherTexts
                                                            ?.mediaURL
                                                }}
                                                showError={props.showError}
                                                optionDisabled={props.disabled}
                                                isLive={props.isLive}
                                                existsInPreviousPackage={
                                                    props
                                                        .previousPackageQuestion
                                                        ?.optionSettings
                                                        ?.showOther
                                                }
                                            />
                                        )}
                                    </>
                                </SortableContainerWrapper>
                                {props.question.showNotApplicableOption &&
                                    !props.settings?.hideNotApplicable && (
                                        <SortableContainerWrapperWithTopMargin>
                                            <Option
                                                languages={props.languages}
                                                shape="round"
                                                showAdvancedMode={false}
                                                isBuiltInOption
                                                question={props.question}
                                                optionType={
                                                    OPTION_TYPES.TEXT_ONLY
                                                }
                                                option={{
                                                    label: 'Not Applicable',
                                                    value: NA_OPTION_VALUE,
                                                    texts: props.question
                                                        .notApplicableTexts
                                                }}
                                                updateOption={(
                                                    keyName,
                                                    value
                                                ) => {}}
                                                deleteOption={async () => {
                                                    const { isConfirmed } =
                                                        await getConfirmationBox(
                                                            {
                                                                showCloseButton:
                                                                    true,
                                                                showCancelButton:
                                                                    true,
                                                                title: 'Delete Option?',
                                                                message:
                                                                    'This action cannot be undone.'
                                                            }
                                                        );
                                                    if (isConfirmed) {
                                                        props.setQuestion(
                                                            'showNotApplicableOption',
                                                            false
                                                        );
                                                    }
                                                }}
                                                updateOptionTextsByLanguage={(
                                                    value: string,
                                                    language: string
                                                ) =>
                                                    props.setMultimediaText(
                                                        'notApplicableTexts',
                                                        value,
                                                        language,
                                                        0,
                                                        MULTIMEDIA_TYPES.TEXT
                                                    )
                                                }
                                                index={-1}
                                                disabled // props for sortableElement HOC
                                                errorObject={{
                                                    texts: props.error
                                                        ?.notApplicableTexts
                                                }}
                                                showError={props.showError}
                                                optionDisabled={props.disabled}
                                                isLive={props.isLive}
                                                existsInPreviousPackage={
                                                    props
                                                        .previousPackageQuestion
                                                        ?.showNotApplicableOption
                                                }
                                            />
                                        </SortableContainerWrapperWithTopMargin>
                                    )}
                                {/* </div> */}
                                {questionType === QTYPES.VIRTUAL ? (
                                    <>
                                        <span
                                            className="options--container--body--total-options"
                                            data-tip="total options"
                                            data-for="total-options"
                                        >
                                            <span>{totalNumOptions}</span>
                                            total options
                                        </span>

                                        <ReactTooltip
                                            id="total-options"
                                            aria-haspopup="true"
                                            effect="solid"
                                            place="top"
                                            backgroundColor="#595959"
                                            className="longContent"
                                        >
                                            {
                                                HELPER_TOOLTIP_MAPPING[
                                                    'Total Options'
                                                ]
                                            }
                                        </ReactTooltip>
                                    </>
                                ) : (
                                    <span className="options--container--body--total-options">
                                        <span>{totalNumOptions}</span>
                                        options in total
                                    </span>
                                )}

                                {!props.settings?.hideEditDesign &&
                                    !editDesignEmpty && (
                                        <Popover
                                            isOpen={showEditDesign}
                                            onClickOutside={() =>
                                                setShowEditDesign(false)
                                            }
                                            align="end"
                                            position={['left']}
                                            padding={10}
                                            content={
                                                <div className="options--container--popover--container">
                                                    <div className="options--container--popover--header">
                                                        <span className="options--container--popover--header--title">
                                                            Edit Design
                                                        </span>
                                                        <span
                                                            className="options--container--popover--header--close"
                                                            onClick={() =>
                                                                setShowEditDesign(
                                                                    false
                                                                )
                                                            }
                                                        >
                                                            <UilMultiply
                                                                size={16}
                                                            />
                                                        </span>
                                                    </div>
                                                    {!textOnly && (
                                                        <div className="options--container--popover--menu">
                                                            <div className="options--container--header--item ">
                                                                <div className="options--container--header--item--title">
                                                                    Image
                                                                    Display
                                                                </div>
                                                                <div className="options--container--header--item--body">
                                                                    <Menu
                                                                        items={
                                                                            imageDisplay
                                                                        }
                                                                        selectedItem={
                                                                            props
                                                                                .question
                                                                                .imageDisplay
                                                                        }
                                                                        onClickItem={(
                                                                            item
                                                                        ) =>
                                                                            props.setQuestion(
                                                                                'imageDisplay',
                                                                                item.value
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            props.disabled
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {!textOnly &&
                                                        hasCustomSizing && (
                                                            <div className="options--container--popover--menu">
                                                                <div className="options--container--header--item ">
                                                                    <div className="options--container--header--item--title">
                                                                        Option
                                                                        Size
                                                                    </div>
                                                                    <div className="options--container--header--item--body">
                                                                        <Menu
                                                                            items={
                                                                                optionSize
                                                                            }
                                                                            selectedItem={
                                                                                optionSettings?.size ||
                                                                                OPTION_SIZE.AUTO
                                                                            }
                                                                            onClickItem={(
                                                                                item
                                                                            ) =>
                                                                                updateOptionSettings(
                                                                                    'size',
                                                                                    item.value
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                props.disabled
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    {!hasIconSize && (
                                                        <div className="options--container--popover--menu">
                                                            <div className="options--container--header--item ">
                                                                <div className="options--container--header--item--title">
                                                                    Options Per
                                                                    Row
                                                                    (Desktop)
                                                                </div>
                                                                <div className="options--container--header--item--body">
                                                                    <Menu
                                                                        items={
                                                                            hasCustomSizing
                                                                                ? determineValidOptionsPerRow(
                                                                                      optionDisplayedInRow
                                                                                  )
                                                                                : optionDisplayedInRow
                                                                        }
                                                                        selectedItem={
                                                                            optionSettings?.numColumns ||
                                                                            0
                                                                        }
                                                                        onClickItem={(
                                                                            item
                                                                        ) =>
                                                                            updateOptionSettings(
                                                                                'numColumns',
                                                                                item.value
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            props.disabled
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {!hasIconSize && (
                                                        <div className="options--container--popover--menu">
                                                            <div className="options--container--header--item ">
                                                                <div className="options--container--header--item--title">
                                                                    Options Per
                                                                    Row (Mobile)
                                                                </div>
                                                                <div className="options--container--header--item--body">
                                                                    <Menu
                                                                        items={
                                                                            hasCustomSizing
                                                                                ? determineValidOptionsPerRow(
                                                                                      optionDisplayedInRow.filter(
                                                                                          (
                                                                                              opt
                                                                                          ) =>
                                                                                              opt.value <=
                                                                                              5
                                                                                      )
                                                                                  )
                                                                                : optionDisplayedInRow.filter(
                                                                                      (
                                                                                          opt
                                                                                      ) =>
                                                                                          opt.value <=
                                                                                          5
                                                                                  )
                                                                        }
                                                                        selectedItem={
                                                                            optionSettings?.numColumnsMobile ||
                                                                            0
                                                                        }
                                                                        onClickItem={(
                                                                            item
                                                                        ) =>
                                                                            updateOptionSettings(
                                                                                'numColumnsMobile',
                                                                                item.value
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            props.disabled
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        >
                                            <button
                                                className="btn btn--xs btn--black options--container--body--btn-design"
                                                onClick={() =>
                                                    setShowEditDesign(
                                                        (prevState) =>
                                                            !prevState
                                                    )
                                                }
                                            >
                                                EDIT DESIGN
                                            </button>
                                        </Popover>
                                    )}
                            </div>
                        </div>
                    </>
                ) : (
                    <BatchEditor
                        editorState={editorState}
                        setEditorState={setEditorState}
                        handleSubmit={handleBatchOption}
                        options={props.question.options}
                        handleBatchImageUpload={handleBatchImageUpload}
                    />
                )}
                {props.isLive && (
                    <p>
                        <span className="font-semibold">Note:</span> Options
                        cannot be rearranged for live projects and can only be
                        edited or added to protect your previously collected
                        data. If necessary, contact our technical team for any
                        changes you would like made
                    </p>
                )}
                {!props.settings?.hideHeader && (
                    <div className="options--container--header--container row">
                        <div className="options--container--header--item col-md-4">
                            <div className="options--container--header--item--title">
                                {name} Formats
                            </div>
                            <div className="options--container--header--item--body">
                                <Menu
                                    disabled={
                                        props.question.piping ||
                                        !!props.settings?.forceOptionType ||
                                        showBatchEdit ||
                                        props.disabled
                                    }
                                    items={optionType}
                                    selectedItem={selectedOptionType}
                                    onClickItem={(item) =>
                                        props.setQuestion(
                                            'selectedOptionType',
                                            item.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                        <div className="options--container--header--item col-md-5">
                            {!props.settings?.hideOptionSettings && (
                                <>
                                    <div className="options--container--header--item--title">
                                        {name} Settings
                                    </div>
                                    <div className="row">
                                        {!props.settings?.hideMultiple && (
                                            <div
                                                className={classnames(
                                                    'options--container--header--item--body checkbox',
                                                    { disabled: props.disabled }
                                                )}
                                            >
                                                <CustomCheckbox
                                                    text={
                                                        questionType ===
                                                            QTYPES.SINGLE_CHOICE &&
                                                        'Select Multiple'
                                                    }
                                                    defaultChecked={
                                                        questionType ===
                                                            QTYPES.MULTIPLE_CHOICE ||
                                                        props.settings
                                                            ?.forceMultiple
                                                    }
                                                    onChange={(e) => {
                                                        props.setQuestion(
                                                            'type',
                                                            e.target.checked
                                                                ? QTYPES.MULTIPLE_CHOICE
                                                                : QTYPES.SINGLE_CHOICE
                                                        );
                                                        props.setQuestion(
                                                            'showContinueButton',
                                                            e.target.checked
                                                        );
                                                        setFocusState(true);
                                                    }}
                                                    value="qtype"
                                                    disabled={
                                                        props.settings
                                                            ?.forceMultiple ||
                                                        props.disabled
                                                    }
                                                    body={multipleChoiceCheckboxBody()}
                                                />
                                            </div>
                                        )}
                                        {props.settings?.showMultiplePopup && (
                                            <div
                                                className={classnames(
                                                    'options--container--header--item--body checkbox',
                                                    { disabled: props.disabled }
                                                )}
                                            >
                                                <CustomCheckbox
                                                    text={
                                                        props.question.popupCards.every(
                                                            (card) =>
                                                                card.type ===
                                                                QTYPES.POPUP_CARD
                                                        ) && 'Select Multiple'
                                                    }
                                                    defaultChecked={props.question.popupCards.every(
                                                        (card) =>
                                                            card.type ===
                                                            QTYPES.MULTI_POPUP_CARD
                                                    )}
                                                    onChange={(e) => {
                                                        props.updatePopupCardType(
                                                            e.target.checked
                                                                ? QTYPES.MULTI_POPUP_CARD
                                                                : QTYPES.POPUP_CARD
                                                        );
                                                        props.setQuestion(
                                                            'showContinueButton',
                                                            e.target.checked
                                                        );
                                                        setFocusState(true);
                                                    }}
                                                    value="qtype"
                                                    disabled={
                                                        props.settings
                                                            ?.forceMultiple ||
                                                        props.disabled
                                                    }
                                                    body={multipleChoiceCheckboxBody()}
                                                />
                                            </div>
                                        )}
                                        <div
                                            className={classnames(
                                                'options--container--header--item--body checkbox',
                                                { disabled: props.disabled }
                                            )}
                                        >
                                            <CustomCheckbox
                                                text="Randomize"
                                                defaultChecked={
                                                    props.question.random
                                                }
                                                onChange={(e) =>
                                                    props.setQuestion(
                                                        'random',
                                                        e.target.checked
                                                    )
                                                }
                                                value="random"
                                                disabled={
                                                    props.question.piping ||
                                                    props.disabled
                                                }
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {!props.settings?.hideAdvanceMode && (
                            <div
                                className="options--container--header--item col-md-3 flex"
                                data-tip="custom show"
                                data-for="advanced-mode-helper"
                                data-delay-show="500"
                                style={{ marginTop: '28px' }}
                            >
                                <Toggle
                                    checked={showAdvancedMode}
                                    disabled={
                                        props.question.piping || props.disabled
                                    }
                                    onChange={() =>
                                        setShowAdvancedMode((mode) => !mode)
                                    }
                                    label="ADVANCED MODE"
                                />
                                <ReactTooltip
                                    className="tooltip"
                                    id="advanced-mode-helper"
                                    aria-haspopup="true"
                                    effect="solid"
                                    place="top"
                                >
                                    <span>
                                        This will enable advanced features such
                                        as conditional logic and custom labels
                                    </span>
                                </ReactTooltip>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotifyHOC(Options);
